import { Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { CircleAlert, Minus } from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import FlexBox from 'components/flex-box';
import LoadingSpinner from 'components/loading-spinner';

import Drawer, { DrawerHandle } from 'components/drawer';
import { MySupplyPlanParams } from 'views/order-feed/features/my-supply-plan/types/my-supply-plan-params.type';

import { useQueryClient } from '@tanstack/react-query';
import CreateOrderForm, {
  DefaultFormValues,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/orders/form/create-order-form';
import { useOrdersState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/orders-context';
import { useCreateOrder } from 'views/order-feed/features/my-supply-plan/hooks/use-create-order';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import { Recommendation } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/types/recommendation-assortment.type';

type Props = { recommendation: Recommendation };

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.orders';

const ModalTitle: React.FC<Props> = ({ recommendation }) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={3}>
      <Box>
        <Typography fontWeight={'bold'}>
          {intl.formatMessage(
            {
              id: `${PAGE_NAME_ID}.modals.skippedDialog.title`,
              defaultMessage: 'orderStatus', // what was this supposed to be?,
            },
            {
              type: recommendation.manuallySkipped ? 'Manual' : 'Auto',
            }
          )}
        </Typography>
      </Box>
      <IconButton sx={{ backgroundColor: theme.palette.darkBlue.o25, cursor: 'unset' }} disableRipple disableTouchRipple size="small">
        <Minus color="black" size={17} />
      </IconButton>
    </Box>
  );
};

const SkippedDialog: React.FC<Props> = ({ recommendation }) => {
  const intl = useIntl();
  const { buildOrderObject, createOFDOrder } = useCreateOrder();
  const params = useParams<MySupplyPlanParams>();
  const drawerRef = useRef<DrawerHandle>(null);
  const { orderSettingsQuery } = useOrdersState();

  const queryClient = useQueryClient();

  const defaultFormValues: DefaultFormValues = useMemo(
    () => ({
      dispatchDate: recommendation.dispatch,
      plantId: recommendation.plantId,
      vessel: recommendation.vessel,
      orderLines: !recommendation.products?.length
        ? [
            {
              itemNumber: '',
              orderQuantity: 0,
            },
          ]
        : recommendation.products.map((product) => ({
            orderQuantity: product.quantity,
            itemNumber: product.productNumber,
          })),
      siteErpId: params.siteErpId,
    }),
    [recommendation]
  );

  const handleSubmit = (data: CreateOrderFormValues) => {
    createOFDOrder.mutate(
      { data: buildOrderObject(data) },
      {
        onSuccess: () => {
          [['getOrderAssortment'], ['getCustomerAssortment'], ['getRecommendationAssortment']].forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey });
          });
          drawerRef.current?.close();
        },
      }
    );
  };

  return (
    <Drawer
      ref={drawerRef}
      title={<ModalTitle recommendation={recommendation} />}
      button={
        <Box display={'flex'} justifyContent={'end'}>
          <Button size="small" variant="outlined" disabled={orderSettingsQuery.data?.isUnderMaintenance}>
            <FormattedMessage id={`${PAGE_NAME_ID}.list.createOrderBtn`} values={{ type: '' }} />
          </Button>
        </Box>
      }>
      <Box display={'flex'} flexDirection={'column'} width={600}>
        <Box>
          {false && <LoadingSpinner overlay />}
          <FlexBox>
            <CreateOrderForm onSubmit={handleSubmit} showOrderDetails defaultFormValues={defaultFormValues}>
              <Box borderRadius={2} p={5} sx={{ background: '#FFEAD6' }} display={'flex'} gap={2}>
                <Box display={'flex'} alignItems={'center'}>
                  <CircleAlert size={20} />
                </Box>
                <Box>
                  <Typography fontWeight={'bold'}>
                    {intl.formatMessage(
                      { id: `${PAGE_NAME_ID}.modals.confirmedDialog.message` },
                      {
                        underlined: (
                          <u>
                            <FormattedMessage id={`${PAGE_NAME_ID}.modals.confirmedDialog.underlinedMessage`} />
                          </u>
                        ),
                      }
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box display={'flex'} justifyContent={'flex-end'}>
                <Button
                  startIcon={createOFDOrder.isPending ? <CircularProgress color="inherit" size={18} /> : null}
                  type="submit"
                  disabled={createOFDOrder.isPending || orderSettingsQuery.data?.isUnderMaintenance}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.list.createOrderBtn`} />
                </Button>
              </Box>
            </CreateOrderForm>
          </FlexBox>
        </Box>
      </Box>
    </Drawer>
  );
};

export default SkippedDialog;
